import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { useId } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/endpoints'
import { Card, Empty, ErrorState, Loading } from '../components/StateViews'
import { formatNumber, intermediaryLabel } from '../format'
import { useUrlFilters } from '../hooks/useUrlFilters'

const INITIAL_FILTERS = { q: '', section: '', province: '', legal_form: '' }
const SECTIONS = ['A', 'B', 'C', 'D', 'E', 'F', 'UE']

export function Search() {
  const [filters, updateFilters] = useUrlFilters(INITIAL_FILTERS)
  const ids = { q: useId(), section: useId(), province: useId() }

  // One condition for both the query and the empty state: they used to disagree
  // when only the legal form was set from the URL.
  const hasCriteria = Object.values(filters).some((value) => value !== '')

  const results = useInfiniteQuery({
    queryKey: ['intermediaries', filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => api.intermediaries(filters, pageParam, signal),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: hasCriteria,
    placeholderData: keepPreviousData,
  })

  const rows = results.data?.pages.flatMap((page) => page.data) ?? []

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Ricerca</h1>
        <p className="text-sm text-slate-500">
          Ricerca sulle ragioni sociali. Delle persone fisiche compare solo il numero di iscrizione.
        </p>
      </div>

      <Card>
        <div className="grid gap-3 p-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <label htmlFor={ids.q} className="sr-only">
              Ragione sociale o numero di iscrizione
            </label>
            <input
              id={ids.q}
              type="search"
              value={filters.q}
              onChange={(event) => updateFilters({ q: event.target.value })}
              placeholder="ragione sociale o numero di iscrizione"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor={ids.section} className="sr-only">
              Sezione
            </label>
            <select
              id={ids.section}
              value={filters.section}
              onChange={(event) => updateFilters({ section: event.target.value })}
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
            >
              <option value="">tutte le sezioni</option>
              {SECTIONS.map((section) => (
                <option key={section}>{section}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={ids.province} className="sr-only">
              Provincia
            </label>
            <input
              id={ids.province}
              value={filters.province}
              onChange={(event) =>
                updateFilters({ province: event.target.value.toUpperCase().slice(0, 2) })
              }
              placeholder="provincia"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
        </div>
      </Card>

      <Card>
        {!hasCriteria ? (
          <Empty title="Scrivi qualcosa per cercare." hint="Anche solo una sezione o una provincia." />
        ) : results.isPending ? (
          <Loading />
        ) : results.isError ? (
          <ErrorState error={results.error} onRetry={() => void results.refetch()} />
        ) : rows.length === 0 ? (
          <Empty
            title="Nessun intermediario trovato."
            hint="Prova con meno filtri o con una parola sola."
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[42rem] text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Numero
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Intermediario
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Sezione
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Natura
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Provincia
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.registration_number} className="hover:bg-slate-50">
                      <td className="px-4 py-2 tabular-nums">
                        <Link
                          to={`/intermediaries/${row.registration_number}`}
                          className="underline decoration-slate-300"
                        >
                          {row.registration_number}
                        </Link>
                      </td>
                      <td className="px-4 py-2">{intermediaryLabel(row)}</td>
                      <td className="px-4 py-2">{row.section}</td>
                      <td className="px-4 py-2">{row.legal_form}</td>
                      <td className="px-4 py-2">{row.province ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
              <span>{formatNumber(rows.length)} risultati</span>
              {results.hasNextPage && (
                <button
                  type="button"
                  onClick={() => void results.fetchNextPage()}
                  disabled={results.isFetchingNextPage}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50"
                >
                  {results.isFetchingNextPage ? 'carico…' : 'carica altri'}
                </button>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
